/**
 * Cost Import — admin upload UI for AWS CUR / Azure / GCP CSV exports
 * =====================================================================
 * Deliberately simple: file picker → choose provider → upload → see
 * dedup-aware results. Same brand as the rest of the authed surface.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Upload, ArrowLeft, FileSpreadsheet, Loader2, RefreshCcw, Cloud,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtMoney = (n) => {
  if (!n && n !== 0) return '$0';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `$${(a / 1_000).toFixed(1)}K`;
  return `$${Math.round(a).toLocaleString()}`;
};

const PROVIDERS = [
  { key: 'aws-cur', label: 'AWS CUR (Cost & Usage Report)', hint: 'Export from AWS BCM Data Exports — schema v2 or legacy v1.' },
  { key: 'azure',   label: 'Azure Cost Export',             hint: 'Export from Azure Cost Management → Exports.' },
  { key: 'gcp',     label: 'GCP Billing Export (CSV)',      hint: 'BigQuery billing export downloaded as CSV.' },
];

const CostImport = () => {
  const [provider, setProvider] = useState('aws-cur');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/cost-import/summary`, auth());
      setSummary(data);
    } catch (e) {
      console.debug('summary unavailable:', e?.message);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  const upload = async () => {
    if (!file) return toast.error('Pick a CSV first');
    setBusy(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/cost-import/${provider}`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setLastResult(r.data);
      toast.success(`Imported · ${r.data.rows_inserted} new line(s) · ${r.data.rows_skipped_duplicate} dedup'd`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      refresh();
    } catch (e) {
      toast.error('Import failed', { description: e?.response?.data?.detail || e?.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="cost-import-page">
      <Link to="/dashboard" className="text-sm text-slate-300 hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-link">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Cloud className="w-7 h-7 text-emerald-500" />
            Multi-cloud Cost Import
          </h1>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Upload your AWS CUR / Azure Cost Export / GCP Billing CSV and we'll normalise it into one
            FOCUS-aligned cost ledger. Idempotent — re-uploading the same file is a no-op.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="refresh-summary-btn">
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Summary */}
      {summary && summary.totals?.lines > 0 && (
        <Card data-testid="summary-card">
          <CardHeader>
            <CardTitle className="text-base">Imported so far</CardTitle>
            <CardDescription>{summary.totals.lines.toLocaleString()} cost lines · {fmtMoney(summary.totals.total_billed_usd)} billed</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {summary.by_provider.map((p) => (
              <div key={p.provider} className="p-3 rounded-md border border-border" data-testid={`provider-summary-${p.provider}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <Badge variant="outline" className="uppercase text-[11px]">{p.provider}</Badge>
                  <span className="text-xs text-slate-300 tabular-nums">{p.lines.toLocaleString()} lines</span>
                </div>
                <p className="text-xl font-bold tabular-nums">{fmtMoney(p.total_billed_usd)}</p>
                <p className="text-[11px] text-slate-300">{p.earliest_period} → {p.latest_period}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Uploader */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-500" /> Upload a CSV
          </CardTitle>
          <CardDescription>50MB max per file. Headers must match the export's native schema (no manual editing required).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-300 mb-2 block">Provider</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {PROVIDERS.map((p) => (
                <button key={p.key} type="button" onClick={() => setProvider(p.key)}
                  data-testid={`provider-${p.key}`}
                  className={`text-left p-3 rounded-md border transition-all ${
                    provider === p.key
                      ? 'border-emerald-500/40 bg-emerald-500/10'
                      : 'border-border hover:border-emerald-500/30 hover:bg-muted/50'
                  }`}>
                  <p className="font-semibold text-sm">{p.label}</p>
                  <p className="text-[11px] text-slate-300 mt-0.5">{p.hint}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-300 mb-2 block">CSV file</label>
            <input
              ref={fileRef} type="file" accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="file-input"
              className="block text-sm w-full file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-emerald-500/10 file:text-emerald-500 hover:file:bg-emerald-500/20"
            />
            {file && (
              <p className="text-xs text-slate-300 mt-2">
                <FileSpreadsheet className="w-3.5 h-3.5 inline mr-1" />
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
          <Button onClick={upload} disabled={busy || !file}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            data-testid="upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Import</>}
          </Button>
        </CardContent>
      </Card>

      {/* Last result */}
      {lastResult && (
        <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="last-result">
          <CardContent className="p-5 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">{lastResult.filename} · {lastResult.provider.toUpperCase()}</p>
              <p className="text-sm text-slate-300 mt-1">
                Parsed <strong>{lastResult.rows_parsed.toLocaleString()}</strong> rows ·{' '}
                Inserted <span className="text-emerald-500 font-semibold">{lastResult.rows_inserted.toLocaleString()}</span> ·{' '}
                Dedup'd <span className="text-amber-500 font-semibold">{lastResult.rows_skipped_duplicate.toLocaleString()}</span> ·{' '}
                Total billed <span className="font-mono">{fmtMoney(lastResult.total_billed_cost_usd)}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-[11px] text-slate-300 italic">
        Manual upload only for v1. Auto-pull from your S3 / Storage Account / BigQuery is on the roadmap and most enterprise teams prefer the upload model anyway (no cross-account IAM required).
      </p>
    </div>
  );
};

export default CostImport;
